import { Table, Button } from "react-bootstrap";
import { formatearMonto } from "../../utils/planillaUtils";
import { FaCog, FaCheckCircle } from "react-icons/fa";

const PlanillaTable = ({ resultados, tipoPlanilla, regimenEmpresa, config = {}, onAbrirAjustes }) => {
  if (!resultados || !resultados.detalle) return null;

  console.log("📊 [PlanillaTable] Renderizando resultados:", {
    total_empleados: resultados.detalle.length,
    primer_empleado_descuentos: resultados.detalle[0]?.descuentos,
    primer_empleado_adelantos_monto: resultados.detalle[0]?.descuentos?.adelantos,
    primer_empleado_adelantos_array: resultados.detalle[0]?.adelantos,
  });

  // Obtener claves únicas de bonos para columnas dinámicas
  const bonosKeys = new Set();
  resultados.detalle.forEach((d) => {
    if (d.bonos) {
      Object.keys(d.bonos).forEach((key) => bonosKeys.add(key));
    }
  });
  const columnasBonos = Array.from(bonosKeys).map((key) => ({
    key: `bonos.${key}`,
    label: key,
    isMonto: true,
    grupo: "Ingresos",
  }));

  // Definir columnas dinámicas basadas en el tipo de planilla
  const columnasIngresos = [
    {
      key: "sueldo_base",
      label: "Sueldo Base",
      isMonto: true,
      grupo: "Ingresos",
    },
    ...columnasBonos, // Incluir columnas de bonos siempre si existen
    ...(tipoPlanilla === "MENSUAL"
      ? [
        {
          key: "asignacion_familiar",
          label: "Asignación Familiar",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "bonificaciones_construccion_civil",
          label: "Bonificaciones Construcción Civil",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "vacaciones",
          label: "Vacaciones",
          isMonto: true,
          grupo: "Ingresos",
        },
      ]
      : []),
    ...(tipoPlanilla === "MENSUAL"
      ? [
        {
          key: "ingresos.horas_extras_25",
          label: "H.E. 25%",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "ingresos.horas_extras_35",
          label: "H.E. 35%",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "ingresos.horas_extras_100",
          label: "H.E. 100%",
          isMonto: true,
          grupo: "Ingresos",
        },
      ]
      : []),
    ...(tipoPlanilla === "GRATIFICACION"
      ? [
        {
          key: "gratificacion",
          label: "Gratificación",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "bonificacion_extraordinaria",
          label: "Bonificación Extraordinaria",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "meses_computables",
          label: "Meses Computables",
          isMonto: false,
          grupo: "Ingresos",
        },
      ]
      : []),
    ...(tipoPlanilla === "CTS"
      ? [
        { key: "cts", label: "CTS", isMonto: true, grupo: "Ingresos" },
        {
          key: "meses_computables",
          label: "Meses Computables",
          isMonto: false,
          grupo: "Ingresos",
        },
      ]
      : []),

    ...(tipoPlanilla === "LIQUIDACION"
      ? [
        {
          key: "salario_mes_cese",
          label: "Salario Mes Cese",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "gratificacion",
          label: "Gratificación Trunca",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "cts",
          label: "CTS Trunca",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "vacaciones",
          label: "Vacaciones Truncas",
          isMonto: true,
          grupo: "Ingresos",
        },
        {
          key: "indemnizacion",
          label: "Indemnización",
          isMonto: true,
          grupo: "Ingresos",
        },
      ]
      : []),

    {
      key: "total_ingresos_bruto",
      label: "Total Ingresos Bruto",
      isMonto: true,
      grupo: "Ingresos",
    },

    {
      key: "total_ingresos_afectos",
      label: "Total Ingresos Afectos",
      isMonto: true,
      grupo: "Ingresos",
    },
  ];

  // Columnas de descuentos detalladas
  const columnasDescuentos = [
    {
      key: "descuentos.sistema_pension.fondo",
      label: "Fondo AFP",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.sistema_pension.onp",
      label: "ONP",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.sistema_pension.comision",
      label: "Comisión AFP",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.sistema_pension.seguro",
      label: "Seguro AFP",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.essalud_vida",
      label: "EsSalud Vida",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.renta_5ta",
      label: "Renta 5ta",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.descuento_faltas",
      label: "Descuento Faltas",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.descuento_dso",
      label: "Descuento DSO",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.descuento_permisos_sin_goce",
      label: "Descuento Permisos S/Goce",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.descuento_tardanzas",
      label: "Descuento Tardanzas",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "descuentos.adelantos",
      label: "Adelantos",
      isMonto: true,
      grupo: "Descuentos",
    },
    {
      key: "total_descuentos",
      label: "Total Descuentos",
      isMonto: true,
      grupo: "Descuentos",
    },
  ];

  // Columnas de aportaciones detalladas
  const columnasAportaciones = [
    {
      key: "aportaciones.salud", // Nueva clave virtual
      label: (config.tipo_seguro_micro === 'SIS' || resultados.detalle.some(d => d.aportaciones?.sis > 0 || d.aportaciones?.essalud === 15)) ? 'Salud (SIS)' : 'EsSalud (9%)',
      isMonto: true,
      grupo: "Aportaciones",
    },
    {
      key: "aportaciones.sctr_salud",
      label: "SCTR Salud",
      isMonto: true,
      grupo: "Aportaciones",
    },
    {
      key: "aportaciones.sctr_pension",
      label: "SCTR Pensión",
      isMonto: true,
      grupo: "Aportaciones",
    },
    {
      key: "aportaciones.vida_ley",
      label: "Vida Ley",
      isMonto: true,
      grupo: "Aportaciones",
    },
    {
      key: "total_aportaciones",
      label: "Total Aportaciones",
      isMonto: true,
      grupo: "Aportaciones",
    },
  ];

  // Columnas de provisiones
  const columnasProvisiones = [
    {
      key: "provisiones.vacaciones",
      label: "Prov. Vacaciones",
      isMonto: true,
      grupo: "Provisiones",
    },
    {
      key: "provisiones.essalud_vacaciones",
      label: (config.tipo_seguro_micro === 'SIS' || resultados.detalle.some(d => d.aportaciones?.essalud === 15)) ? 'Salud Vac (SIS)' : 'EsSalud Vac (9%)',
      isMonto: true,
      grupo: "Provisiones",
    },
    {
      key: "provisiones.gratificaciones",
      label: "Prov. Gratificaciones",
      isMonto: true,
      grupo: "Provisiones",
    },
    {
      key: "provisiones.bono_extraordinario",
      label: (config.tipo_seguro_micro === 'SIS' || resultados.detalle.some(d => d.aportaciones?.essalud === 15)) ? 'Bono Grati (SIS)' : 'Bono Grati (9%)',
      isMonto: true,
      grupo: "Provisiones",
    },
    {
      key: "provisiones.cts",
      label: "Prov. CTS",
      isMonto: true,
      grupo: "Provisiones",
    },
    {
      key: "provisiones.total",
      label: "Total Provisiones",
      isMonto: true,
      grupo: "Provisiones",
    },
  ];

  const columnasGenerales = [
    { key: "neto_pagar", label: "Neto a Pagar", isMonto: true, grupo: "Resumen" },
    { key: "costo_laboral_total", label: "Costo Laboral Total", isMonto: true, grupo: "Resumen" },
  ];

  const todasLasColumnas = [
    ...columnasIngresos,
    ...columnasDescuentos,
    ...columnasAportaciones,
    ...columnasProvisiones,
    ...columnasGenerales,
  ];

  // Definir colores tenues para los grupos
  const coloresGrupos = {
    Ingresos: "#e8f5e8",
    Descuentos: "#ffebee",
    Aportaciones: "#e3f2fd",
    Provisiones: "#fff3e0",
    Resumen: "#fcf8e3",
  };

  const grupos = [
    {
      label: "Ingresos",
      columnas: columnasIngresos,
      color: coloresGrupos["Ingresos"],
    },
    {
      label: "Descuentos",
      columnas: columnasDescuentos,
      color: coloresGrupos["Descuentos"],
    },
    {
      label: "Aportaciones",
      columnas: columnasAportaciones,
      color: coloresGrupos["Aportaciones"],
    },
    {
      label: "Provisiones",
      columnas: columnasProvisiones,
      color: coloresGrupos["Provisiones"],
    },
    {
      label: "Resumen",
      columnas: columnasGenerales,
      color: coloresGrupos["Resumen"],
    },
  ];

  // Función auxiliar para obtener valor anidado correctamente
  const getNestedValue = (obj, path) => {
    if (!obj) return 0;
    return path.split(".").reduce((current, key) => {
      if (current === undefined || current === null) return undefined;
      return current[key];
    }, obj);
  };

  return (
    <div
      className="table-responsive"
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "0.375rem",
        maxHeight: "600px",
        width: "100%",
        maxWidth: "100%", // Force container to not exceed parent
        overflowX: "auto", // Explicit horizontal scroll
        overflowY: "auto", // Explicit vertical scroll
        position: "relative",
      }}
    >
      <Table
        className="table table-bordered table-hover mb-0"
        style={{ width: "max-content", minWidth: "100%" }}
      >
        <thead className="bg-light">
          <tr
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#f8f9fa",
            }}
          >
            <th
              style={{
                position: "sticky",
                left: 0,
                zIndex: 4,
                backgroundColor: "#f8f9fa",
                width: "50px",
                textAlign: "center"
              }}
              rowSpan={2}
            >
              Acc.
            </th>
            <th
              style={{
                position: "sticky",
                left: "50px",
                zIndex: 3,
                backgroundColor: "#f8f9fa",
                minWidth: "200px",
              }}
              rowSpan={2}
            >
              Empleado
            </th>
            {grupos.map((grupo) => (
              <th
                key={grupo.label}
                colSpan={grupo.columnas.length}
                className="text-center"
                style={{ backgroundColor: grupo.color }}
              >
                {grupo.label}
              </th>
            ))}
          </tr>
          <tr
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#f8f9fa",
            }}
          >
            {todasLasColumnas.map((col) => (
              <th
                key={col.key}
                style={{
                  minWidth: "130px",
                  backgroundColor: coloresGrupos[col.grupo],
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {resultados.detalle
            .filter((detalle) => detalle.empleado_id && detalle.nombres)
            .map((detalle) => (
              <tr key={detalle.empleado_id}>
                <td
                  style={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "white",
                    zIndex: 2,
                    textAlign: "center"
                  }}
                >
                  <Button 
                    variant={detalle.ajustes ? "primary" : "outline-secondary"} 
                    size="sm" 
                    className="p-1"
                    onClick={() => onAbrirAjustes(detalle)}
                  >
                    {detalle.ajustes ? <FaCheckCircle /> : <FaCog />}
                  </Button>
                </td>
                <td
                  style={{
                    position: "sticky",
                    left: "50px",
                    backgroundColor: "white",
                    zIndex: 1,
                    minWidth: "200px",
                  }}
                >
                  {detalle.nombres}
                </td>

                {todasLasColumnas.map((col) => (
                  <td
                    key={col.key}
                    className="text-end"
                    style={{ backgroundColor: coloresGrupos[col.grupo] }}
                  >
                    {(() => {
                      const value = col.key === "aportaciones.salud"
                        ? (detalle.aportaciones?.sis || detalle.aportaciones?.essalud || 0)
                        : col.key.startsWith("bonos.")
                          ? (detalle.bonos?.[col.key.split(".")[1]] ?? 0)
                          : (getNestedValue(detalle, col.key) ?? 0);
                      
                      if (col.key === "descuentos.adelantos" && value > 0) {
                        console.log(`🎯 [PlanillaTable] Adelanto encontrado para ${detalle.nombres}: ${value}`);
                      } else if (col.key === "descuentos.adelantos") {
                         // console.log(`🔍 [PlanillaTable] Adelanto 0 para ${detalle.nombres}`);
                      }

                      return col.isMonto
                        ? formatearMonto(value)
                        : (isNaN(value) ? "0" : value.toFixed(0));
                    })()}
                  </td>
                ))}
              </tr>
            ))}

          <tr
            className="fw-bold"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              borderTop: "2px solid #dee2e6",
            }}
          >
            <td style={{ position: "sticky", left: 0, backgroundColor: "white" }}></td>
            <td
              style={{
                position: "sticky",
                left: "50px",
                backgroundColor: "white",
              }}
            >
              TOTALES
            </td>

            {todasLasColumnas.map((col) => (
              <td
                key={col.key}
                className="text-end"
                style={{ backgroundColor: coloresGrupos[col.grupo] }}
              >
                {col.key.startsWith("bonos.")
                  ? formatearMonto(
                    resultados.detalle.reduce(
                      (sum, d) =>
                        sum + (d.bonos?.[col.key.split(".")[1]] ?? 0),
                      0
                    )
                  )
                  : col.isMonto
                    ? formatearMonto(
                      resultados.detalle.reduce((sum, d) => {
                        const v = col.key === "aportaciones.salud"
                          ? (d.aportaciones?.sis || d.aportaciones?.essalud || 0)
                          : getNestedValue(d, col.key);
                        return sum + (v ?? 0);
                      }, 0)
                    )
                    : (() => {
                      const total = resultados.detalle.reduce(
                        (sum, d) => sum + (getNestedValue(d, col.key) ?? 0),
                        0
                      );
                      return isNaN(total) ? "0" : total.toFixed(0);
                    })()}
              </td>
            ))}
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

export default PlanillaTable;
